import {
  knowledgeGapCodeLabels,
  queueReasonCodeLabels,
  type KnowledgeGapCode,
  type QueueReasonCode,
} from "../../../shared/src/domain/service-profile-queue.js";
import type {
  Projection,
  Queue,
  QueueItem,
  ServiceCategory,
} from "../api/types.js";

export const categoryLabels: Record<ServiceCategory, string> = {
  pipette_calibration: "Pipettes",
  balance_calibration: "Balances",
};

const monthLabels = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const seasonLabels: Record<NonNullable<Projection["timingSeason"]>, string> = {
  spring: "Spring",
  summer: "Summer",
  fall: "Fall",
  winter: "Winter",
};

export function categoryLabel(category: ServiceCategory): string {
  return categoryLabels[category];
}

const queueLabels: Record<Queue["queueCode"], string> = {
  NOW: "Needs attention",
  COMING: "Coming up",
  INVESTIGATE: "Explore",
  SLEEPING: "Sleeping",
  DISQUALIFIED_SERVICE_NEED: "Review service fit",
};

export function queueLabel(queueCode: Queue["queueCode"]): string {
  return queueLabels[queueCode];
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "Not recorded";
  const date = new Date(`${value.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function padDateTimePart(value: number): string {
  return String(value).padStart(2, "0");
}

/** Format an instant for an HTML datetime-local input in the browser timezone. */
export function formatDateTimeLocal(value: Date): string {
  return [
    `${value.getFullYear()}-${padDateTimePart(value.getMonth() + 1)}-${padDateTimePart(value.getDate())}`,
    `${padDateTimePart(value.getHours())}:${padDateTimePart(value.getMinutes())}`,
  ].join("T");
}

/** Parse an HTML datetime-local value as local browser time, then serialize its instant. */
export function localDateTimeToISOString(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u.exec(value);
  if (!match) throw new Error("Enter a valid local date and time.");
  const [, year, month, day, hour, minute] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
  if (
    Number.isNaN(date.valueOf()) ||
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day) ||
    date.getHours() !== Number(hour) ||
    date.getMinutes() !== Number(minute)
  ) {
    throw new Error("Enter a valid local date and time.");
  }
  return date.toISOString();
}

export function formatLocalDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
}

export function formatObservedTiming(projection: Projection): string {
  switch (projection.timingPrecision) {
    case "exact_date":
      return projection.timingExactDate
        ? formatDate(projection.timingExactDate)
        : "Exact date recorded";
    case "month":
      return projection.timingMonth
        ? `${monthLabels[projection.timingMonth - 1] ?? "Month"}${projection.timingYear ? ` ${projection.timingYear}` : ""}`
        : "Month recorded";
    case "date_range":
      return projection.timingRangeStart && projection.timingRangeEnd
        ? `${formatDate(projection.timingRangeStart)} – ${formatDate(projection.timingRangeEnd)}`
        : "Date range recorded";
    case "season":
      return projection.timingSeason
        ? `${seasonLabels[projection.timingSeason]}${projection.timingYear ? ` ${projection.timingYear}` : ""}`
        : "Season recorded";
    case "relative":
      return projection.timingRelativeState
        ? projection.timingRelativeState.replaceAll("_", " ")
        : "Relative timing";
    case "unknown":
    case null:
      return "Timing unknown";
  }
}

export function formatDerivedWindow(projection: Projection): string | null {
  if (!projection.derivedDueStartDate || !projection.derivedDueEndDate)
    return null;
  if (projection.derivedDueStartDate === projection.derivedDueEndDate) {
    return formatDate(projection.derivedDueStartDate);
  }
  return `${formatDate(projection.derivedDueStartDate)} – ${formatDate(projection.derivedDueEndDate)}`;
}

export function formatQuantity(projection: Projection): string {
  if (projection.currentQuantityValue === null) return "Quantity unknown";
  const qualifier =
    projection.currentQuantityQualifier === "exact" ? "Exact" : "Approx.";
  return `${qualifier} ${projection.currentQuantityValue}`;
}

export function formatProvider(projection: Projection): string {
  const name = projection.currentProviderName?.trim();
  return name ? name : "Provider unknown";
}

/** True when the projection records no comparable timing fact at all. */
export function timingIsUnknown(projection: Projection): boolean {
  return (
    projection.timingPrecision === null ||
    projection.timingPrecision === "unknown"
  );
}

/**
 * Presentation-only summary of a profile that has recorded nothing yet. A
 * profile with no timing, quantity, or provider is an exploration starting
 * point rather than work, so a caller can present what is still missing instead
 * of a grid of empty values. Queue membership always comes from the server.
 */
export function isUnrecordedProfile(item: QueueItem): boolean {
  const projection = item.projection;
  return (
    timingIsUnknown(projection) &&
    projection.currentQuantityValue === null &&
    !projection.currentProviderName?.trim()
  );
}

export function queueReason(queue: Queue): string {
  return (
    queueReasonCodeLabels[queue.queueReasonCode as QueueReasonCode] ??
    "This record needs review."
  );
}

const queueReasonShortLabels: Record<QueueReasonCode, string> = {
  unsupported_accreditation_requirement: "Accreditation unsupported",
  relative_overdue: "Overdue per latest information",
  due_soon_window_active: "Due-soon window active",
  likely_overdue_after_due_soon_window: "Due-soon window passed",
  attention_date_due: "Attention date is due",
  timing_unknown: "Timing unknown",
  attention_date_within_coming_horizon: "Within attention horizon",
  attention_date_beyond_coming_horizon: "Beyond planning horizon",
};

/**
 * A cell-sized reason for a dense desktop row. It answers "why is this row in
 * front of me" without exposing the rule that classified it; the full sentence
 * from `queueReason` stays available on wider surfaces.
 */
export function queueReasonShort(queue: Queue): string {
  return (
    queueReasonShortLabels[queue.queueReasonCode as QueueReasonCode] ??
    "Needs review"
  );
}

/**
 * A compact timing cell. Unlike `formatObservedTiming`, which spells out
 * "Timing unknown", a table cell already carries its column header.
 */
export function formatTimingShort(projection: Projection): string {
  if (timingIsUnknown(projection)) return "Unknown";
  if (projection.timingPrecision === "month" && projection.timingMonth) {
    return `${monthLabels[projection.timingMonth - 1] ?? "Month"}${
      projection.timingYear ? ` ${projection.timingYear}` : ""
    }`;
  }
  if (projection.timingPrecision === "season" && projection.timingSeason) {
    return `${seasonLabels[projection.timingSeason]}${
      projection.timingYear ? ` ${projection.timingYear}` : ""
    }`;
  }
  return formatObservedTiming(projection);
}

export function formatQuantityShort(projection: Projection): string {
  if (projection.currentQuantityValue === null) return "Unknown";
  const prefix = projection.currentQuantityQualifier === "exact" ? "" : "~";
  return `${prefix}${projection.currentQuantityValue}`;
}

export function formatProviderShort(projection: Projection): string {
  return projection.currentProviderName?.trim() || "Unknown";
}

/**
 * Place is what a salesperson navigates by: the building plus the room, not a
 * database identifier. Parent breadcrumb names come from the server.
 */
export function placeLabel(item: QueueItem): string {
  const room = item.lab.roomOrSuite?.trim();
  return room ? `${item.location.name} · ${room}` : item.location.name;
}

/** Organization plus building, for the place grouping header. */
export function placeGroupLabel(item: QueueItem): string {
  const parents = item.parentLocationBreadcrumb
    .map((entry) => entry.name)
    .join(" / ");
  return [item.organization.name, parents, item.location.name]
    .filter(Boolean)
    .join(" · ");
}

export function gapLabel(gap: string): string {
  return (
    knowledgeGapCodeLabels[gap as KnowledgeGapCode] ??
    gap
      .replaceAll("_unknown", " unknown")
      .replaceAll("_", " ")
      .replace(/^./u, (letter) => letter.toUpperCase())
  );
}

/**
 * Why a search row matched. The server returns the canonical field it searched,
 * such as `room_or_suite`; a salesperson should read a plain phrase instead, and
 * an unknown field degrades to readable words rather than leaking an identifier.
 */
const matchFieldLabels: Record<string, string> = {
  name: "Name",
  email: "Email",
  phone: "Phone",
  title: "Title",
  department: "Department",
  unit_type: "Unit type",
  room_or_suite: "Room or suite",
};

export function matchFieldLabel(field: string): string {
  return (
    matchFieldLabels[field] ??
    field.replaceAll("_", " ").replace(/^./u, (letter) => letter.toUpperCase())
  );
}

/** Verification state as a quiet word rather than a raw enum value. */
export function verificationLabel(status: string): string {
  if (status === "verified") return "Verified";
  if (status === "unverified") return "Unverified";
  return status.replaceAll("_", " ");
}

export function breadcrumbLabel(item: QueueItem): string {
  const parent = item.parentLocationBreadcrumb
    .map((entry) => entry.name)
    .join(" / ");
  return [item.organization.name, parent, item.location.name, item.lab.name]
    .filter(Boolean)
    .join(" / ");
}

/**
 * The service facts as one line of notebook prose: category, quantity, timing,
 * provider. Field Notebook Modern reads as black ink facts rather than a grid of
 * labelled cells, so the ledger row and the pocket card share this phrasing.
 * Unknowns are stated plainly instead of being dressed up as values.
 */
export function serviceFacts(item: QueueItem): string {
  const projection = item.projection;
  const parts: string[] = [categoryLabel(item.serviceCategory)];
  if (projection.currentQuantityValue !== null) {
    parts.push(formatQuantityShort(projection));
  }
  parts.push(formatTimingShort(projection));
  if (projection.currentProviderName?.trim()) {
    parts.push(formatProviderShort(projection));
  }
  return parts.join(" · ");
}

/** What is still missing, phrased as context rather than a task. */
export function missingFacts(item: QueueItem): string | null {
  if (item.queue.knowledgeGaps.length === 0) return null;
  return item.queue.knowledgeGaps.slice(0, 3).map(gapLabel).join(" · ");
}

/**
 * Whether this row has earned the notebook's red margin annotation. Red marks
 * what genuinely needs attention now, so it stays rare enough to keep meaning.
 * Unknown information is context, never an alarm.
 */
export function needsAttentionMark(item: QueueItem): boolean {
  return (
    item.queue.queueCode === "NOW" ||
    item.queue.queueCode === "DISQUALIFIED_SERVICE_NEED"
  );
}

export function createClientRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `capture-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
